import { Service } from '@/shared/types';
import { supabaseService } from '@/shared/services/supabaseService';
import { authService } from '@/shared/services/realData/auth';

/**
 * Service operations
 * Handles all service-related database operations
 */
export const servicesService = {
  /**
   * Get all services for the current user
   */
  async getServices(): Promise<Service[]> {
    const supabaseUserId = await authService.getCurrentUserSupabaseId();
    if (!supabaseUserId) throw new Error('User not authenticated or profile not found');

    // Use the service service
    const { getServices } = await import('@/shared/services/services/serviceService');
    return getServices(supabaseUserId);
  },

  /**
   * Add a new service
   */
  async addService(service: Omit<Service, 'id'>): Promise<void> {
    const supabaseUserId = await authService.getCurrentUserSupabaseId();
    if (!supabaseUserId) throw new Error('User not authenticated or profile not found');

    // Use the service service
    const { createService } = await import('@/shared/services/services/serviceService');
    await createService(supabaseUserId, service);
  },

  /**
   * Update an existing service
   */
  async updateService(service: Service): Promise<void> {
    const { updateService } = await import('@/shared/services/services/serviceService');
    await updateService(service.id, service);
  },

  /**
   * Delete a service
   */
  async deleteService(serviceOrId: string | Service): Promise<void> {
    const serviceId = typeof serviceOrId === 'string' ? serviceOrId : serviceOrId.id;
    // Use the service service
    const { deleteService } = await import('@/shared/services/services/serviceService');
    await deleteService(serviceId);
  },

  /**
   * Get a service by ID
   */
  async getServiceById(serviceId: string): Promise<Service | null> {
    const { data, error } = await supabaseService.client
      .from('services')
      .select('*')
      .eq('id', serviceId)
      .single();

    if (error) return null;
    return data;
  },

  /**
   * Create a new service (alias for addService)
   */
  async createService(serviceData: Partial<Service>): Promise<Service> {
    const supabaseUserId = await authService.getCurrentUserSupabaseId();
    if (!supabaseUserId) throw new Error('User not authenticated or profile not found');

    // Use the service service
    const { createService } = await import('@/shared/services/services/serviceService');
    return createService(supabaseUserId, serviceData as Omit<Service, 'id' | 'createdAt'>);
  }
};
